import React, { useState } from 'react';
import { useMediaQuery } from 'usehooks-ts';

import { useActiveSection } from '../../hooks/useActiveSection';
import { useSiteNavHeight } from '../../hooks/useSiteNavHeight';
import { Footer } from '../Footer';
import {
  DocsTableOfContents,
  getTocIds,
  type DocsTocLink,
} from './DocsTableOfContents';
import { fontSans } from './docsTypography';

interface DocsLayoutProps {
  /** Ordered list of TOC links displayed in the left rail and used to
   *  drive scroll-spy. */
  tocLinks: readonly DocsTocLink[];
  /**
   * Lead block rendered at the top of the article column (typically
   * the `DocH1` + `DocSubtitle` pair). Rendered ABOVE the mobile-only
   * TOC, which the layout inserts automatically — keeps the visual
   * order "Title → Subtitle → Mobile TOC → Body" consistent across
   * pages without each consumer having to repeat the dance.
   */
  header?: React.ReactNode;
  /** Article body content (sections, code blocks, CTAs, etc.). */
  children: React.ReactNode;
}

/**
 * Two-column docs shell used by `Tutorial` and
 * `BusinessApiDocumentation`. Owns the navbar-height measurement,
 * the shared scroll container, the sticky desktop TOC, the
 * mobile-only TOC (auto-inserted between `header` and `children`),
 * and the footer.
 *
 * The wrapping `<div>` exposes the navbar height as `--site-nav-h`
 * so any descendant can read it via CSS. The inner `<main>` is the
 * full remaining viewport height on desktop, and the inner
 * two-column wrapper IS the scroll container — putting
 * `overflow-y-auto` on a single ancestor (not on the aside or
 * article individually) keeps wheel events forwarding correctly and
 * avoids the "dead zone" effect that splitting scroll containers
 * caused. See the inline comment in `DocsTableOfContents` for why
 * the TOC does not get its own scrollbar.
 *
 * The `useActiveSection` `root` flips between the scroll-container
 * element (desktop) and `null` (mobile / `document` scroll), with
 * the desktop `topOffsetPx` accounting only for the slim sticky
 * gap above the scrollport and the mobile offset accounting for the
 * site navbar instead.
 */
export function DocsLayout({ tocLinks, header, children }: DocsLayoutProps) {
  const navHeight = useSiteNavHeight();

  // Tracked in state via a callback ref so attaching the element
  // triggers a re-render and `useActiveSection` picks up the correct
  // root on first paint. A plain `useRef` would leave the hook stuck
  // observing against the viewport.
  const [scrollContainerEl, setScrollContainerEl] =
    useState<HTMLElement | null>(null);
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const activeId = useActiveSection(getTocIds(tocLinks), {
    root: isDesktop ? scrollContainerEl : null,
    topOffsetPx: isDesktop ? 24 : navHeight + 16,
  });

  return (
    <div
      className={`
        bg-gray-25 text-stone-800 antialiased
        ${fontSans}
      `}
      style={{ ['--site-nav-h' as never]: `${navHeight}px` }}
    >
      <main
        className={`
          pb-16
          lg:flex lg:h-[calc(100dvh-var(--site-nav-h,80px))] lg:pb-0
        `}
      >
        <div
          ref={setScrollContainerEl}
          className={`
            mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 pt-6
            sm:px-6 sm:pt-8
            lg:h-full lg:flex-row lg:items-start lg:gap-8 lg:overflow-y-auto
            lg:overscroll-contain lg:px-6 lg:py-3
            xl:gap-10
          `}
        >
          <aside
            className={`
              hidden shrink-0
              lg:sticky lg:top-0 lg:block lg:h-full lg:w-56
              xl:w-60
            `}
          >
            <DocsTableOfContents links={tocLinks} activeId={activeId} />
          </aside>

          <article
            className={`
              w-full max-w-[55rem] min-w-0 flex-1 rounded-sm border
              border-stone-200/80 bg-white px-6 py-8
              shadow-[0_1px_3px_rgba(0,0,0,0.06)]
              sm:px-10 sm:py-10
              md:px-14
            `}
          >
            {header}

            {/* Mobile-only TOC — the left aside is hidden below `lg`,
                so without this, mobile readers would lose the section
                navigation entirely. */}
            <div className="mb-8 lg:hidden">
              <DocsTableOfContents links={tocLinks} activeId={activeId} />
            </div>

            {children}
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
}
